import React, { ChangeEventHandler, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './setInput.module.scss'
import { SideEnum } from 'types/api/enums'
import { isPositiveInt } from 'utils'
import classNames from 'classnames'

type SetScore = {
  side1: { set: number | null; tiebreak: number | null }
  side2: { set: number | null; tiebreak: number | null }
}
type SideScore = keyof SetScore
type SetInputScoreType = keyof SetScore[SideScore]

interface SideScoreInputProps {
  score: SetScore[SideScore]
  onChangeSetInput: ChangeEventHandler<HTMLInputElement>
  onChangeTiebreakInput: ChangeEventHandler<HTMLInputElement>
  setIsInputVisible: (isVisible: boolean) => void
  isInputVisible: boolean
  isEditable?: boolean
  hasTiebreak?: boolean
  hasError?: boolean
  isWinner?: boolean
  className?: string
  ariaLabelSideNumber?: number
}

const SideScoreInput = ({
  score,
  onChangeSetInput,
  onChangeTiebreakInput,
  setIsInputVisible,
  isInputVisible = false,
  hasTiebreak = false,
  isWinner = false,
  isEditable = false,
  hasError = false,
  className = '',
  ariaLabelSideNumber,
}: SideScoreInputProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const handleFocus = useCallback(() => {
    setIsInputVisible(true)
    setIsFocused(true)
  }, [setIsInputVisible])
  const handleBlur = useCallback(() => {
    setIsInputVisible(false)
    setIsFocused(false)
  }, [setIsInputVisible])
  return (
    <p
      className={classNames(styles.inputGroup, className, {
        [styles.isWinner]: isWinner,
        [styles.validationError]: hasError,
        [styles.focused]: isFocused,
        [styles.expanded]: isInputVisible && hasTiebreak,
      })}
    >
      <input
        type='text'
        value={score.set === null ? '' : score.set}
        onChange={onChangeSetInput}
        className={classNames(styles.inputSet, {
          [styles.isVisible]: isInputVisible,
        })}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={!isEditable}
        aria-label={
          ariaLabelSideNumber && ariaLabelSideNumber > 0
            ? `input score for side ${ariaLabelSideNumber}`
            : undefined
        }
        inputMode='numeric'
      />
      {hasTiebreak && (
        <input
          type='text'
          value={score.tiebreak === null ? '' : score.tiebreak}
          onChange={onChangeTiebreakInput}
          className={classNames(styles.inputTiebreak, {
            [styles.isVisible]: isInputVisible,
          })}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={!isEditable}
          aria-label={
            ariaLabelSideNumber && ariaLabelSideNumber > 0
              ? `input tiebreaker score for side ${ariaLabelSideNumber}`
              : undefined
          }
          inputMode='numeric'
        />
      )}
      <span
        className={classNames(styles.scoreDisplay, {
          [styles.isVisible]: !isInputVisible,
        })}
        aria-hidden
      >
        {score.set}
        {hasTiebreak && (
          <span className={styles.tb}>{score.tiebreak === null ? '' : score.tiebreak}</span>
        )}
      </span>
    </p>
  )
}

interface SetInputProps {
  score: SetScore
  pointsMaxSet: number
  pointsMaxTiebreak?: number
  header: string
  winningSide?: SideEnum
  onScoreChange: ({
    scoreType,
    side,
    newScore,
  }: {
    scoreType: SetInputScoreType
    side: 'side1' | 'side2'
    newScore: number | null
  }) => void
  hasTiebreak?: boolean
  isEditable?: boolean
  hasError?: boolean
}

const SetInput = ({
  score,
  pointsMaxSet,
  header,
  winningSide,
  onScoreChange,
  hasTiebreak = false,
  pointsMaxTiebreak = 0,
  isEditable = false,
  hasError = false,
}: SetInputProps) => {
  const { t: tSocialMatches } = useTranslation('strSocialMatches')
  const [isInputFocused, setIsInputFocused] = useState(false)
  const isTiebreakIncomplete =
    hasTiebreak && (score.side1.tiebreak === null || score.side2.tiebreak === null)
  const createHandler = useCallback(
    (side: SideScore, scoreType: keyof SetScore[SideScore]): ChangeEventHandler<HTMLInputElement> =>
      e => {
        const newVal = e.target.value
        const pointsToWin = scoreType === 'set' ? pointsMaxSet : pointsMaxTiebreak
        if (newVal === '') {
          onScoreChange({ scoreType, side, newScore: null })
        } else if (
          isPositiveInt(newVal) &&
          parseInt(newVal) >= 0 &&
          parseInt(newVal) <= pointsToWin
        ) {
          onScoreChange({ scoreType, side, newScore: parseInt(newVal) })
        }
      },
    [pointsMaxSet, pointsMaxTiebreak, onScoreChange]
  )

  const handleInputSetSide1 = useMemo(() => createHandler('side1', 'set'), [createHandler])
  const handleInputSetSide2 = useMemo(() => createHandler('side2', 'set'), [createHandler])
  const handleInputTiebreakSide1 = useMemo(
    () => createHandler('side1', 'tiebreak'),
    [createHandler]
  )
  const handleInputTiebreakSide2 = useMemo(
    () => createHandler('side2', 'tiebreak'),
    [createHandler]
  )

  return (
    <section className={classNames(styles.wrapper, { [styles.isDisabled]: !isEditable })}>
      <header className={classNames(styles.header, { [styles.validationError]: hasError })}>
        {header}
        {((isInputFocused && hasTiebreak) || isTiebreakIncomplete) &&
          ` - ${tSocialMatches('postScore.matchScore.label.tiebreak')}`}
      </header>
      <div className={styles.inputGroupWrapper}>
        <SideScoreInput
          score={score.side1}
          onChangeSetInput={handleInputSetSide1}
          onChangeTiebreakInput={handleInputTiebreakSide1}
          setIsInputVisible={setIsInputFocused}
          isInputVisible={isInputFocused || isTiebreakIncomplete}
          hasTiebreak={hasTiebreak}
          isWinner={winningSide === SideEnum.SIDE1}
          isEditable={isEditable}
          hasError={hasError}
          ariaLabelSideNumber={1}
        />
        <SideScoreInput
          score={score.side2}
          onChangeSetInput={handleInputSetSide2}
          onChangeTiebreakInput={handleInputTiebreakSide2}
          setIsInputVisible={setIsInputFocused}
          isInputVisible={isInputFocused || isTiebreakIncomplete}
          hasTiebreak={hasTiebreak}
          isWinner={winningSide === SideEnum.SIDE2}
          isEditable={isEditable}
          hasError={hasError}
          ariaLabelSideNumber={2}
        />
      </div>
    </section>
  )
}

export default SetInput
